import React, { useState, useEffect, useRef } from 'react';
import logger from '../utils/logger';
import './DebugConsole.css';

interface LogEntry {
  id: number;
  level: string;
  message: string;
  data?: any;
  timestamp: string;
}

interface DebugConsoleProps {
  maxEntries?: number;
}

const DebugConsole: React.FC<DebugConsoleProps> = ({ maxEntries = 100 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const logIdCounter = useRef(0);
  const consoleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Check if visual debug mode is enabled
    const urlParams = new URLSearchParams(window.location.search);
    const debugMode = urlParams.get('debug');
    
    if (debugMode === 'visual' || debugMode === 'true') {
      setIsVisible(true);
      
      // Override logger methods to capture logs
      const originalMethods = {
        info: logger.info.bind(logger),
        warn: logger.warn.bind(logger),
        error: logger.error.bind(logger),
        debug: logger.debug.bind(logger)
      };

      const captureLog = (level: string, message: string, data?: any) => {
        const logEntry: LogEntry = {
          id: logIdCounter.current++,
          level,
          message,
          data,
          timestamp: new Date().toLocaleTimeString()
        };

        setLogs(prevLogs => {
          const newLogs = [...prevLogs, logEntry];
          // Keep only the most recent entries
          return newLogs.slice(-maxEntries);
        });

        // Scroll to bottom
        setTimeout(() => {
          if (consoleRef.current && !isMinimized) {
            consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
          }
        }, 10);
      };

      // Monkey patch logger methods
      logger.info = (message: string, data?: any, category?: string) => {
        originalMethods.info(message, data, category);
        captureLog('info', message, data);
      };

      logger.warn = (message: string, data?: any, category?: string) => {
        originalMethods.warn(message, data, category);
        captureLog('warn', message, data);
      };

      logger.error = (message: string, data?: any, category?: string) => {
        originalMethods.error(message, data, category);
        captureLog('error', message, data);
      };

      logger.debug = (message: string, data?: any, category?: string) => {
        originalMethods.debug(message, data, category);
        captureLog('debug', message, data);
      };

      // Log initial status
      logger.info('Visual Debug Console activated', {
        wsUrl: logger.getStatus().wsUrl,
        remoteLoggingEnabled: logger.getStatus().remoteLoggingEnabled,
        connected: logger.getStatus().connected
      });

      // Cleanup on unmount
      return () => {
        logger.info = originalMethods.info;
        logger.warn = originalMethods.warn;
        logger.error = originalMethods.error;
        logger.debug = originalMethods.debug;
      };
    }
  }, [maxEntries, isMinimized]);

  if (!isVisible) return null;

  const clearLogs = () => {
    setLogs([]);
    logger.info('Debug console cleared');
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  const getLevelClass = (level: string) => {
    switch (level) {
      case 'error': return 'log-error';
      case 'warn': return 'log-warn';
      case 'info': return 'log-info';
      case 'debug': return 'log-debug';
      default: return '';
    }
  };

  const loggerStatus = logger.getStatus();

  return (
    <div className={`debug-console ${isMinimized ? 'minimized' : ''}`}>
      <div className="debug-console-header">
        <span className="debug-console-title">Debug Console</span>
        <div className="debug-console-status">
          {loggerStatus.remoteLoggingEnabled && (
            <span className={`connection-status ${loggerStatus.connected ? 'connected' : 'disconnected'}`}>
              {loggerStatus.connected ? '🟢' : '🔴'} Remote
            </span>
          )}
        </div>
        <div className="debug-console-controls">
          <button onClick={clearLogs} className="debug-btn">Clear</button>
          <button onClick={toggleMinimize} className="debug-btn">
            {isMinimized ? '▲' : '▼'}
          </button>
        </div>
      </div>
      
      {!isMinimized && (
        <div className="debug-console-body" ref={consoleRef}>
          {logs.length === 0 ? (
            <div className="debug-console-empty">No logs yet...</div>
          ) : (
            logs.map(log => (
              <div key={log.id} className={`log-entry ${getLevelClass(log.level)}`}>
                <span className="log-time">[{log.timestamp}]</span>
                <span className="log-level">[{log.level.toUpperCase()}]</span>
                <span className="log-message">{log.message}</span>
                {log.data && (
                  <pre className="log-data">{JSON.stringify(log.data, null, 2)}</pre>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default DebugConsole;